import { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { Search, Plus, User, LogOut, LayoutDashboard, ChevronDown, Grid3x3, Flame, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { fetchPublicCategories } from "@/lib/api";
import { CATEGORIES } from "@/config/categories";
import MegaMenu from "@/components/public/MegaMenu";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const PublicHeader = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { user, isAdmin, logout } = useAuth();
  const [counts, setCounts] = useState({});
  const [q, setQ] = useState("");
  const [megaOpen, setMegaOpen] = useState(false);
  const closeTimer = useRef(null);

  useEffect(() => {
    fetchPublicCategories()
      .then((d) => {
        const map = {};
        (d.categories || []).forEach((c) => { map[c.category] = c.count; });
        setCounts(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => { setMegaOpen(false); }, [location.pathname]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/suche?q=${encodeURIComponent(q.trim())}`);
  };

  const activeCat = params.slug ? decodeURIComponent(params.slug) : null;

  return (
    <header className="sticky top-0 z-50 w-full backdrop-blur-xl bg-white/80 border-b border-slate-200/70 shadow-sm relative" data-testid="main-header">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {/* Top row */}
        <div className="flex items-center gap-3 sm:gap-4 h-16">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
            <img src="/wizard-icon.png" alt="dealzauber" className="h-9 w-9 rounded-[10px] shadow-sm" />
            <span className="font-outfit font-extrabold text-lg tracking-tight hidden sm:block">
              <span className="text-slate-900">deal</span><span className="ai-gradient-text">zauber</span>
            </span>
          </Link>

          <form onSubmit={onSearch} className="flex-1 max-w-xl relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Wonach suchst du? z.B. Kopfhörer, Gaming…"
              className="w-full bg-slate-100/80 rounded-full pl-10 pr-4 py-2.5 text-sm placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-[#FF62A5]/40 focus:bg-white transition-all"
              data-testid="header-search-input"
            />
          </form>

          <Link
            to="/deal-einreichen"
            className="bg-gradient-to-r from-[#A259FF] via-[#FF62A5] to-[#FF8A3D] text-white font-outfit font-bold rounded-full px-4 py-2.5 text-sm hover:shadow-lg hover:shadow-pink-500/25 active:scale-95 transition-all inline-flex items-center gap-1.5 shrink-0"
            data-testid="submit-deal-cta"
          >
            <Plus className="h-4 w-4" strokeWidth={2.5} /> <span className="hidden md:inline">Deal posten</span>
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1.5 shrink-0 outline-none" data-testid="user-menu-trigger">
                <div className="h-9 w-9 rounded-full ai-gradient-bg text-white flex items-center justify-center font-outfit font-bold text-sm">
                  {user.name?.charAt(0).toUpperCase()}
                </div>
                <ChevronDown className="h-4 w-4 text-slate-400 hidden sm:block" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
                  <p className="text-xs text-slate-400 truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profil")} data-testid="menu-profile"><User className="h-4 w-4" /> Mein Profil</DropdownMenuItem>
                {isAdmin && <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><LayoutDashboard className="h-4 w-4" /> Admin-Cockpit</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-rose-600 focus:text-rose-600" data-testid="menu-logout"><LogOut className="h-4 w-4" /> Abmelden</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/login" className="text-sm font-semibold text-slate-700 hover:text-slate-900 px-2 py-2 hidden sm:block" data-testid="login-link">Anmelden</Link>
              <Link to="/registrieren" className="text-sm font-outfit font-bold text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-full px-4 py-2.5 transition-all active:scale-95" data-testid="register-link">Registrieren</Link>
            </div>
          )}
        </div>

        {/* Nav row */}
        <nav className="flex items-center gap-1 overflow-x-auto pb-2.5 -mb-px scrollbar-hide" data-testid="category-nav">
          <button
            onClick={() => setMegaOpen(true)}
            onMouseEnter={() => { clearTimeout(closeTimer.current); setMegaOpen(true); }}
            className={`shrink-0 px-3.5 py-1.5 rounded-full text-sm font-bold font-outfit inline-flex items-center gap-1.5 transition-all ${megaOpen ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-800 hover:bg-slate-200"}`}
            data-testid="mega-menu-trigger"
          >
            <Grid3x3 className="h-4 w-4" /> Kategorien <ChevronDown className={`h-4 w-4 transition-transform ${megaOpen ? "rotate-180" : ""}`} />
          </button>

          <Link to="/" className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-semibold inline-flex items-center gap-1 transition-all ${location.pathname === "/" && !activeCat ? "bg-[#FF3B30]/10 text-[#FF3B30]" : "text-slate-600 hover:bg-slate-100"}`} data-testid="nav-all-deals">
            <Flame className="h-4 w-4" /> Alle Deals
          </Link>

          <span className="h-5 w-px bg-slate-200 mx-1 shrink-0" />

          {CATEGORIES.slice(0, 7).map((c) => {
            const active = activeCat === c.name;
            return (
              <Link
                key={c.name}
                to={`/kategorie/${encodeURIComponent(c.name)}`}
                className={`shrink-0 px-3 py-1.5 rounded-full text-sm font-semibold transition-all ${active ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`}
                data-testid={`category-link-${c.name}`}
              >
                {c.name}{counts[c.name] ? <span className="opacity-50 ml-1">{counts[c.name]}</span> : null}
              </Link>
            );
          })}
        </nav>
      </div>

      {megaOpen && (
        <div onMouseLeave={() => { closeTimer.current = setTimeout(() => setMegaOpen(false), 150); }}>
          <MegaMenu counts={counts} onClose={() => setMegaOpen(false)} />
        </div>
      )}
    </header>
  );
};

export default PublicHeader;
